import { ReactNode, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Loader2 } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { useResponsive } from '../../hooks/useResponsive';
import { useFormKeyboardShortcuts } from '../../hooks/useFormKeyboardShortcuts';
import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../../../components/ui/sheet';

interface SecondaryAction {
  label: string;
  onClick: () => void;
  isLoading?: boolean;
}

interface DialogFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit?: () => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  submitLabel?: string;
  cancelLabel?: string;
  isLoading?: boolean;
  isDisabled?: boolean;
  destructive?: boolean;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  hideActions?: boolean;
  /** Hide the Cancel button, leaving the primary action alone (e.g. a terminal
   *  success state where the only sensible action is a single Close). */
  hideCancel?: boolean;
  /** Force fullscreen on all screen sizes (useful for complex forms) */
  forceFullScreen?: boolean;
  /** Auto-focus the first input field when the dialog opens (default: true) */
  autoFocusFirst?: boolean;
  /** Optional secondary action button rendered between Cancel and the primary button */
  secondaryAction?: SecondaryAction;
}

const maxWidthClasses = {
  xs: 'sm:max-w-xs',
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-md',
  lg: 'sm:max-w-lg',
  xl: 'sm:max-w-xl',
  '2xl': 'sm:max-w-2xl',
};

export function DialogForm({
  open,
  onClose,
  onSubmit,
  title,
  description,
  children,
  submitLabel,
  cancelLabel,
  isLoading = false,
  isDisabled = false,
  destructive = false,
  maxWidth = 'sm',
  hideActions = false,
  hideCancel = false,
  forceFullScreen = false,
  autoFocusFirst = true,
  secondaryAction,
}: DialogFormProps) {
  const { t } = useTranslation('common');
  const { isMobile } = useResponsive();
  const resolvedSubmitLabel = submitLabel ?? t('app.save');
  const resolvedCancelLabel = cancelLabel ?? t('app.cancel');
  const isFullScreen = forceFullScreen || isMobile;
  const contentRef = useRef<HTMLDivElement>(null);
  // When there is no description, opt out of Radix's aria-describedby requirement
  // instead of duplicating the title into an sr-only description (which reads as a
  // redundant verbatim subtitle in the accessibility tree). Passing the prop only
  // in the no-description case preserves Radix's own title↔description wiring when
  // a description IS provided. See Radix Dialog `DescriptionWarning`.
  const describedByOptOut = description ? {} : { 'aria-describedby': undefined };

  const { handleKeyDown } = useFormKeyboardShortcuts({
    onSubmit,
    onCancel: onClose,
    isLoading,
  });

  // Focus the first input when dialog opens
  // Called from onOpenAutoFocus which fires after Radix mounts the content
  const focusFirstInput = useCallback(() => {
    if (!autoFocusFirst || !contentRef.current) return;

    // Prioritize text inputs, then textareas, then selects
    // Exclude hidden inputs, disabled inputs, and buttons
    const selectors = [
      'input[type="text"]:not([disabled])',
      'input[type="email"]:not([disabled])',
      'input[type="tel"]:not([disabled])',
      'input[type="number"]:not([disabled])',
      'input[type="password"]:not([disabled])',
      'input:not([type]):not([disabled])',
      'textarea:not([disabled])',
      'select:not([disabled])',
    ];

    for (const selector of selectors) {
      const firstInput = contentRef.current?.querySelector<HTMLElement>(selector);
      if (firstInput && firstInput.offsetParent !== null) {
        // offsetParent check ensures element is visible
        firstInput.focus();
        return;
      }
    }
  }, [autoFocusFirst]);

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen && !isLoading) {
        onClose();
      }
    },
    [onClose, isLoading]
  );

  // Mobile: Full-screen Sheet
  if (isFullScreen) {
    return (
      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetContent
          side="bottom"
          className="flex h-[100dvh] flex-col p-0"
          hideCloseButton
          {...describedByOptOut}
          onKeyDown={handleKeyDown}
          onOpenAutoFocus={(e) => {
            // Prevent Radix's default focus behavior - we handle it ourselves
            if (autoFocusFirst) {
              e.preventDefault();
              // Use double requestAnimationFrame to ensure React has rendered form fields
              requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  focusFirstInput();
                });
              });
            }
          }}
        >
          {/* Header */}
          <div className="flex shrink-0 items-center justify-between border-b bg-background px-4 py-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              disabled={isLoading}
              className="h-11 w-11"
              data-testid="dialog-close"
            >
              <X className="h-4 w-4" />
              <span className="sr-only">{t('app.close')}</span>
            </Button>
            <SheetHeader className="flex-1 px-2">
              <SheetTitle className="text-center text-base">{title}</SheetTitle>
              {description && (
                <SheetDescription className="text-center text-xs">
                  {description}
                </SheetDescription>
              )}
            </SheetHeader>
            {!hideActions && (
              <div className="flex items-center gap-2">
                {secondaryAction && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-11 min-w-11"
                    onClick={secondaryAction.onClick}
                    disabled={isLoading || isDisabled || secondaryAction.isLoading}
                  >
                    {secondaryAction.isLoading && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
                    {secondaryAction.label}
                  </Button>
                )}
                {onSubmit && (
                  <Button
                    size="sm"
                    variant={destructive ? 'destructive' : 'default'}
                    className="h-11 min-w-11"
                    onClick={onSubmit}
                    disabled={isLoading || isDisabled || secondaryAction?.isLoading}
                    data-testid="dialog-submit"
                  >
                    {isLoading && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
                    {resolvedSubmitLabel}
                  </Button>
                )}
              </div>
            )}
          </div>
          {/* Content */}
          <div ref={contentRef} className="flex-1 overflow-y-auto p-4 px-[calc(1rem+4px)] -mx-1">
            {children}
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  // Desktop: Standard Dialog
  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className={cn(
          'sm:max-w-[425px] max-h-[90vh] flex flex-col',
          maxWidthClasses[maxWidth]
        )}
        hideCloseButton
        {...describedByOptOut}
        onKeyDown={handleKeyDown}
        onOpenAutoFocus={(e) => {
          // Prevent Radix's default focus behavior - we handle it ourselves
          if (autoFocusFirst) {
            e.preventDefault();
            // Use double requestAnimationFrame to ensure React has rendered form fields
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                focusFirstInput();
              });
            });
          }
        }}
      >
        <DialogHeader className="flex flex-row items-center justify-between space-y-0 shrink-0">
          <div className="flex-1 space-y-1">
            <DialogTitle>{title}</DialogTitle>
            {description && <DialogDescription>{description}</DialogDescription>}
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            disabled={isLoading}
            className="h-8 w-8"
            data-testid="dialog-close"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">{t('app.close')}</span>
          </Button>
        </DialogHeader>
        <div ref={contentRef} className="py-4 px-1 -mx-1 overflow-y-auto min-h-0">
          {children}
        </div>
        {!hideActions && (
          <DialogFooter className="shrink-0">
            {!hideCancel && (
              <Button
                variant="outline"
                onClick={onClose}
                disabled={isLoading || secondaryAction?.isLoading}
                data-testid="dialog-cancel"
              >
                {resolvedCancelLabel}
              </Button>
            )}
            {secondaryAction && (
              <Button
                variant="outline"
                onClick={secondaryAction.onClick}
                disabled={isLoading || isDisabled || secondaryAction.isLoading}
              >
                {secondaryAction.isLoading && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
                {secondaryAction.label}
              </Button>
            )}
            {onSubmit && (
              <Button
                variant={destructive ? 'destructive' : 'default'}
                onClick={onSubmit}
                disabled={isLoading || isDisabled || secondaryAction?.isLoading}
                data-testid="dialog-submit"
              >
                {isLoading && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
                {resolvedSubmitLabel}
              </Button>
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
